import React, { lazy, useEffect } from "react";
import "./index.scss";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router";

// The landing + search pages are the common entry point, so they stay in the main
// bundle. The builder and checkout flows are heavier (Leaflet maps, the SSE pipeline,
// react-hook-form, the jose JWT lib, TrustPayments) and only reached mid-funnel, so
// they are split into their own lazily-loaded chunks.
import PackageSearchPage from "./pages/productPages/packages/packageSearchPage.jsx";
import HotelOnlySearchPage from "./pages/productPages/hotelOnly/hotelOnlySearchPage.jsx";

const PackageBuilderPage = lazy(
    () =>
        import(/* webpackChunkName: "builder" */ "./pages/productPages/packages/packageBuilderPage.jsx")
);

const HotelOnlyBuilderPage = lazy(
    () =>
        import(/* webpackChunkName: "builder" */ "./pages/productPages/hotelOnly/hotelOnlyBuilderPage.jsx")
);

const PassengerDetailsPage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/paxDetailsPage.jsx")
);
const PaymentPage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/paymentPage.jsx")
);
const ThankYouPage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/thankYouPage.jsx")
);
const RyanAirPage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/ryanAirPage.jsx")
);
const QuotePage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/quotePage.jsx")
);
const AgentConfirmPage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/checkout/agentConfirmPage.jsx")
);
const OpenQuoteMessagePage = lazy(
    () => import(/* webpackChunkName: "checkout" */ "./pages/openQuote/openQuoteMessagePage.jsx")
);

import DetailsPageWrapper from "./pages/wrappers/detailsPageWrapper.jsx";
import CheckoutWrapper from "./pages/wrappers/checkoutWrapper.jsx";
import SiteWrapper from "./pages/wrappers/siteWrapper.jsx";
import IndexPage from "./pages/indexPage.jsx";
import ErrorBoundary from "./helpers/components/errorBoundary.jsx";
import { SUPPORT_PHONE } from "./config/constants.js";
import { matomoRouteChange } from "./helpers/analytics.js";

const errorFallback = (
    <div style={{ padding: "3rem 1rem", textAlign: "center", maxWidth: "32rem", margin: "0 auto" }}>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.75rem" }}>
            Sorry, something went wrong
        </h1>
        <p>
            Please refresh the page and try again. If the problem continues, call our team on{" "}
            <strong>{SUPPORT_PHONE}</strong>.
        </p>
    </div>
);

// Reset the scroll position on every route change. Without this, iOS Safari keeps the
// previous page's scroll offset when navigating to a shorter page and renders a blank
// screen (stuck scrolled past the content) until the user touches the screen.
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

// Matomo page view per route (see helpers/analytics.js). Hotel builder pages (/D/...) send theirs with
// the product view once view_item fires. Keyed on the path only: the search page rewrites the query
// string as filters change, which should not count as new page views.
const MatomoPageViews = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        matomoRouteChange(pathname.startsWith("/D/"));
    }, [pathname]);
    return null;
};

document.addEventListener("DOMContentLoaded", function (event) {
    const root = document.getElementById("reactroot");
    const wrapper = <SiteWrapper />;
    createRoot(root).render(
        <ErrorBoundary fallback={errorFallback}>
            <BrowserRouter>
                <ScrollToTop />
                <MatomoPageViews />
                <Routes>
                    <Route element={wrapper}>
                        <Route index element={<IndexPage />} />
                        <Route
                            path="P/:from/:to/:duration/:earlyStart/:lateStart/:rooms"
                            element={<PackageSearchPage />}
                        ></Route>

                        <Route
                            path="AO/:to/:duration/:earlyStart/:lateStart/:rooms"
                            element={<HotelOnlySearchPage />}
                        ></Route>

                        <Route path="D" element={<DetailsPageWrapper />}>
                            <Route
                                path="P/:from/:property/:startdate/:duration/:rooms/"
                                element={<PackageBuilderPage />}
                            />
                            <Route
                                path="AO/:property/:startdate/:duration/:rooms/"
                                element={<HotelOnlyBuilderPage />}
                            />
                        </Route>

                        <Route path="open-quote/message" element={<OpenQuoteMessagePage />} />

                        <Route path="/checkout/:basketid/" element={<CheckoutWrapper />}>
                            <Route path="passenger-details" element={<PassengerDetailsPage />} />
                            <Route path="payment" element={<PaymentPage />} />
                            <Route path="agent-confirm" element={<AgentConfirmPage />} />
                            <Route path="ryanair" element={<RyanAirPage />} />
                            <Route path="thankyou" element={<ThankYouPage />} />
                            <Route path="quote-done" element={<QuotePage />} />
                        </Route>
                    </Route>
                </Routes>
            </BrowserRouter>
        </ErrorBoundary>
    );
});
